/**
 * Flutter WebView 셸과 React 앱 사이의 유일한 통신 경로.
 *
 * 같은 React 코드가 (1) Flutter WebView 안 (2) 일반 브라우저 두 곳에서 모두
 * 동작해야 한다. 그래서 모든 기능은 네이티브 구현과 **브라우저 폴백**을 쌍으로 갖는다.
 * 앱 전용 화면을 따로 만들지 않는 것이 이 구조의 핵심이다.
 *
 * 셸 쪽 구현: solar-app/lib/webview/bridge.dart
 */

export type NativeCapability =
  | 'takePhoto'
  | 'pickPhoto'
  | 'getLocation'
  | 'openMap'
  | 'getToken'
  | 'setToken'
  | 'clearToken'
  | 'share'
  | 'scanBarcode'
  | 'setBrightness';

export interface Photo {
  /** data:image/jpeg;base64,... */
  dataUrl: string;
  width: number;
  height: number;
  takenAt: string;
  lat?: number;
  lng?: number;
}

export interface Coordinates {
  lat: number;
  lng: number;
  accuracy: number;
}

export interface NativeInfo {
  platform: 'ios' | 'android';
  shellVersion: string;
}

/** flutter_inappwebview 가 window 에 주입하는 핸들러. */
interface FlutterHandler {
  callHandler(name: string, ...args: unknown[]): Promise<unknown>;
}

declare global {
  interface Window {
    flutter_inappwebview?: FlutterHandler;
    __SOLAR_NATIVE__?: NativeInfo;
    /** 셸이 앱 복귀 시 호출한다. */
    __solarOnResume?: () => void;
  }
}

export function isNative(): boolean {
  return typeof window !== 'undefined' && window.__SOLAR_NATIVE__ !== undefined;
}

export function nativeInfo(): NativeInfo | null {
  return (typeof window !== 'undefined' && window.__SOLAR_NATIVE__) || null;
}

class BridgeError extends Error {
  constructor(
    public capability: NativeCapability,
    message: string,
  ) {
    super(message);
    this.name = 'BridgeError';
  }
}

const CALL_TIMEOUT_MS = 60_000;

/**
 * 셸의 응답 봉투. `solar-app/lib/webview/bridge.dart` 가 항상 이 모양으로 답한다.
 *
 * Dart 쪽에서 예외를 그대로 던지면 이쪽 Promise 가 영영 resolve 되지 않으므로,
 * 실패도 정상 응답으로 감싸서 돌려받는다.
 */
type BridgeEnvelope<T> = { ok: true; value: T } | { ok: false; error: string };

async function call<T>(capability: NativeCapability, payload?: unknown): Promise<T> {
  const handler = typeof window !== 'undefined' ? window.flutter_inappwebview : undefined;
  if (!handler) {
    throw new BridgeError(capability, '네이티브 셸이 아닙니다.');
  }

  // 셸이 응답하지 않으면(권한 다이얼로그에서 앱이 죽는 등) 화면이 영구히 멈춘다.
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new BridgeError(capability, '네이티브 응답이 없습니다.')),
      CALL_TIMEOUT_MS,
    );
  });

  let result: unknown;
  try {
    result = await Promise.race([handler.callHandler(capability, payload), timeout]);
  } finally {
    clearTimeout(timer!);
  }

  // 구형 셸이 봉투 없이 값만 보내는 경우까지 받아준다.
  if (result === null || typeof result !== 'object' || !('ok' in result)) {
    return result as T;
  }

  const envelope = result as BridgeEnvelope<T>;
  if (!envelope.ok) throw new BridgeError(capability, envelope.error);
  return envelope.value;
}

// --------------------------------------------------------------------- 카메라
/**
 * 레거시 앱은 사진 17장을 base64 로 인코딩해 요청 본문에 실었다(수십 MB).
 * 여기서는 dataURL 을 받아 즉시 presigned URL 로 S3 에 직접 올린다.
 */
export async function takePhoto(): Promise<Photo> {
  if (isNative()) return required('takePhoto', await call<Photo | null>('takePhoto'));
  return pickPhotoFromInput('environment');
}

export async function pickPhoto(): Promise<Photo> {
  if (isNative()) return required('pickPhoto', await call<Photo | null>('pickPhoto'));
  return pickPhotoFromInput();
}

/** 셸은 사용자가 촬영을 취소하면 null 을 준다 — 오류가 아니라 빈 결과다. */
function required(capability: NativeCapability, photo: Photo | null): Photo {
  if (photo === null) throw new BridgeError(capability, '선택된 사진이 없습니다.');
  return photo;
}

function pickPhotoFromInput(capture?: 'environment'): Promise<Photo> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) input.capture = capture;

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return reject(new Error('선택된 사진이 없습니다.'));

      const reader = new FileReader();
      reader.onerror = () => reject(new Error('사진을 읽지 못했습니다.'));
      reader.onload = () => {
        const dataUrl = String(reader.result);
        const img = new Image();
        img.onload = () =>
          resolve({
            dataUrl,
            width: img.naturalWidth,
            height: img.naturalHeight,
            takenAt: new Date(file.lastModified).toISOString(),
          });
        img.onerror = () => reject(new Error('이미지 형식이 아닙니다.'));
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

// --------------------------------------------------------------------- 위치
export async function getLocation(): Promise<Coordinates> {
  if (isNative()) return call<Coordinates>('getLocation');

  if (!navigator.geolocation) throw new Error('위치 기능을 사용할 수 없습니다.');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
        }),
      (err) => reject(new Error(err.message)),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  });
}

export async function openMap(address: string): Promise<void> {
  if (isNative()) {
    await call<void>('openMap', { address });
    return;
  }
  window.open(`https://map.kakao.com/link/search/${encodeURIComponent(address)}`, '_blank');
}

// --------------------------------------------------------------------- 토큰
/**
 * 네이티브에서는 flutter_secure_storage(Keychain/Keystore)에 저장한다.
 * 브라우저에서는 sessionStorage 를 쓴다 — localStorage 는 XSS 시 영속 탈취 위험이 크다.
 */
const TOKEN_KEY = 'solar.refresh_token';

export async function getToken(): Promise<string | null> {
  if (isNative()) return call<string | null>('getToken');
  return sessionStorage.getItem(TOKEN_KEY);
}

export async function setToken(token: string): Promise<void> {
  if (isNative()) {
    await call<void>('setToken', { token });
    return;
  }
  sessionStorage.setItem(TOKEN_KEY, token);
}

export async function clearToken(): Promise<void> {
  if (isNative()) {
    await call<void>('clearToken');
    return;
  }
  sessionStorage.removeItem(TOKEN_KEY);
}

// --------------------------------------------------------------------- 기타
export async function share(title: string, url: string): Promise<void> {
  if (isNative()) {
    await call<void>('share', { title, url });
    return;
  }
  if (navigator.share) {
    await navigator.share({ title, url });
    return;
  }
  await navigator.clipboard.writeText(url);
}

/** 서명 화면에서 밝기를 올린다. 브라우저에서는 무시된다. */
export async function setBrightness(value: number): Promise<void> {
  if (!isNative()) return;
  await call<void>('setBrightness', { value: Math.min(Math.max(value, 0), 1) });
}

/** 셸이 백그라운드에서 복귀할 때 호출된다 (목록 새로고침용). */
export function onResume(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const previous = window.__solarOnResume;
  window.__solarOnResume = () => {
    previous?.();
    handler();
  };
  document.addEventListener('visibilitychange', onVisible);

  function onVisible() {
    if (document.visibilityState === 'visible') handler();
  }

  return () => {
    window.__solarOnResume = previous;
    document.removeEventListener('visibilitychange', onVisible);
  };
}

export { BridgeError };
